import { isBlank, parseNumericCell } from "./cellValue";
import { GridSelection, selectionRect } from "./gridGeometry";
import { ColumnId, CompiledView } from "./sheetTypes";

/**
 * What the status bar says about the selected block, and what Ctrl+C copies.
 * Count is every selected cell that has something in it; the numeric figures
 * only speak for the cells that actually read as numbers, and the bar says how
 * many those were so nobody reads a Sum as covering the whole selection.
 */

export interface SelectionSummary {
  cells: number;
  filled: number;
  numeric: number;
  sum: number | null;
  average: number | null;
  min: number | null;
  max: number | null;
}

export const EMPTY_SUMMARY: SelectionSummary = {
  cells: 0,
  filled: 0,
  numeric: 0,
  sum: null,
  average: null,
  min: null,
  max: null,
};

/** Two decimals at most, thousands separated - money and counts both read well. */
export function formatSummaryNumber(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function summarizeSelection(
  view: CompiledView,
  selection: GridSelection | null,
): SelectionSummary {
  if (!selection) return EMPTY_SUMMARY;
  const rect = selectionRect(selection);

  let cells = 0;
  let filled = 0;
  let numeric = 0;
  let sum = 0;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  const lastRow = Math.min(rect.bottom, view.rows.length - 1);
  const lastCol = Math.min(rect.right, view.columns.length - 1);

  for (let r = Math.max(0, rect.top); r <= lastRow; r++) {
    const rowCells = view.rows[r].cells;
    for (let c = Math.max(0, rect.left); c <= lastCol; c++) {
      cells++;
      const raw = rowCells[view.columns[c].sourceIndex];
      const value = raw == null ? "" : raw;
      if (isBlank(value)) continue;
      filled++;
      const parsed = parseNumericCell(value);
      if (parsed === null) continue;
      numeric++;
      sum += parsed;
      if (parsed < min) min = parsed;
      if (parsed > max) max = parsed;
    }
  }

  if (numeric === 0) {
    return {
      cells,
      filled,
      numeric: 0,
      sum: null,
      average: null,
      min: null,
      max: null,
    };
  }
  return {
    cells,
    filled,
    numeric,
    sum,
    average: sum / numeric,
    min,
    max,
  };
}

/** The selected block as a matrix, for the clipboard. */
export function selectionToMatrix(
  view: CompiledView,
  selection: GridSelection | null,
): string[][] {
  if (!selection) return [];
  const rect = selectionRect(selection);
  const lastRow = Math.min(rect.bottom, view.rows.length - 1);
  const lastCol = Math.min(rect.right, view.columns.length - 1);

  const out: string[][] = [];
  for (let r = Math.max(0, rect.top); r <= lastRow; r++) {
    const rowCells = view.rows[r].cells;
    const row: string[] = [];
    for (let c = Math.max(0, rect.left); c <= lastCol; c++) {
      const raw = rowCells[view.columns[c].sourceIndex];
      row.push(raw == null ? "" : raw);
    }
    out.push(row);
  }
  return out;
}

/** Row identities covered by the selection - what Hide rows acts on. */
export function selectedRowIds(
  view: CompiledView,
  selection: GridSelection | null,
): number[] {
  if (!selection) return [];
  const rect = selectionRect(selection);
  const ids: number[] = [];
  const lastRow = Math.min(rect.bottom, view.rows.length - 1);
  for (let r = Math.max(0, rect.top); r <= lastRow; r++) {
    ids.push(view.rows[r].sourceIndex);
  }
  return ids;
}

/**
 * The same figures for a whole column of the CURRENT view - what the column
 * menu shows, so that a person can read a total without first selecting 50,000
 * cells. It speaks for the filtered set, and the menu says so.
 */
export function summarizeColumn(
  view: CompiledView,
  columnId: ColumnId,
): SelectionSummary {
  let columnPos = -1;
  for (let i = 0; i < view.columns.length; i++) {
    if (view.columns[i].id === columnId) {
      columnPos = i;
      break;
    }
  }
  if (columnPos < 0 || view.rows.length === 0) return EMPTY_SUMMARY;

  return summarizeSelection(view, {
    kind: "cells",
    anchorRow: 0,
    anchorCol: columnPos,
    focusRow: view.rows.length - 1,
    focusCol: columnPos,
  });
}
